/**
 * Canonical in-app URLs for YAGNI-native records.
 *
 * These are the deep-link shapes the frontend router actually resolves to a
 * detail view. Every backend surface that hands a URL to the agent (tool
 * results, search results, artifacts, notifications) MUST build it here —
 * hand-rolled paths drift when routes collapse (e.g. the retired `/tracker`
 * and `/work/:id` shapes) and the agent then cites links that dead-end on an
 * index page.
 *
 * - A work item opens in its Team's queue with the ticket sheet on it
 *   (`/desks/:deskId?item=:id`). Without the Team in hand, `/work?workItem=`
 *   still resolves: the frontend reads the item to learn its Team and lands
 *   there, as do the older `/work/:id` and `/work/:id/receipt` shapes.
 * - A Library page has no address of its own: its reading view and the
 *   `/knowledge/:pageId` route are gone, and that path now lands on Waiting on
 *   you. A page is read on the ticket it is attached to, so link that ticket
 *   (`workItemUrl`; the backend's `pageHomeUrls` finds it) or give no link.
 * - One pull request's review opens on Waiting on you (`?review=`), keyed by
 *   `pr_state` id.
 */
/**
 * Deep link to a work item. With its Team, the Team's queue with the ticket
 * open; without one, the Work address that redirects there.
 */
export function workItemUrl(workItemId, deskId) {
    if (deskId)
        return `/desks/${encodeURIComponent(deskId)}?item=${encodeURIComponent(workItemId)}`;
    return `/work?workItem=${encodeURIComponent(workItemId)}`;
}
/**
 * Deep link to one pull request's review case.
 *
 * Addressed by `pr_state` id, which is the row identity the review read keys
 * off and survives a case the reviewer has not minted a work item for yet. It
 * rides Waiting on you as a query param: the dialog resolves the case itself,
 * so a link works for a review outside the loaded page or hidden by a filter.
 * The older `/work?review=` shape redirects here.
 *
 * This replaced `/workers/:slug/:deskId/reviews/:prStateId` when the
 * engagement pages retired (ADR-0044, PR 2). The address had to survive the
 * move: a review with no URL cannot be pasted into Slack or attached to a
 * ticket, and an auditable record whose records have no addresses is a
 * weaker claim.
 */
export function reviewCaseUrl(prStateId) {
    return `/waiting?review=${encodeURIComponent(prStateId)}`;
}
/**
 * The pick link: Waiting on you with the Add tickets sheet open and the
 * tracker key already searched. A ticket assigned to yagni that no Team
 * claims replies with this, so the person picks the Team in YAGNI.
 */
export function addTicketUrl(key) {
    return `/waiting?add=1&q=${encodeURIComponent(key)}`;
}
/** Deep link to a Prototype's detail view. */
export function prototypeUrl(prototypeId) {
    return `/prototypes/${encodeURIComponent(prototypeId)}`;
}
/**
 * Deep link to one recorded decision on the Decisions surface (the decision
 * ledger, a first-class destination since 2026-09-09). The retired
 * `/knowledge/decisions/:id` shape still redirects here, so links minted
 * before the move keep landing.
 */
export function decisionUrl(decisionId) {
    return `/decisions/${encodeURIComponent(decisionId)}`;
}
/**
 * Deep link to one Run attempt's detail page. Attempt numbers are the
 * user-facing handle (run UUIDs never appear in URLs); with no runs yet the
 * route falls back to the work item's case file.
 */
export function runUrl(workItemId, attemptNo) {
    return `/runs/${encodeURIComponent(workItemId)}/${attemptNo}`;
}
//# sourceMappingURL=appUrls.js.map